import { GetBy } from '../_app/cuchillo/core/Element';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Maths } from '../_app/cuchillo/utils/Maths';

/**
 * Nav de índice ligado a anchors de la página.
 * Se instancia con el contenedor del nav; calcula progreso según el scroll.
 *
 * @example
 * const bar = new IndexBar(document.querySelector('.report-nav'));
 * // en resize / loop de la página:
 * bar.resize();
 * bar.loop();
 */
export default class IndexBar {
  /**
   * @param {HTMLElement|string} container Nav con los links `#anchor`
   * @param {HTMLElement|string} [content] Contenedor padre del contenido (fin del último capítulo)
   */
  constructor(container, content) {
    this.container = typeof container === 'string'
      ? GetBy.id(container) || GetBy.selector(container)[0]
      : container;

    this.content = typeof content === 'string'
      ? GetBy.id(content) || GetBy.selector(content)[0]
      : content;

    if (!this.content && this.container) {
      this.content = this.container.closest('.page-report__holder')
        || this.container.closest('.page-report')
        || this.container.parentElement;
    }

    this.items = [];
    this.total = 0;
    this._progress = 0;
    this._activeIndex = -1;
    this._enabled = !!this.container;

    if (!this._enabled) {
      console.warn('[IndexBar] container not found');
      return;
    }

    this.reset();
  }

  get progress() {
    return this._progress;
  }

  set progress(value) {
    this._progress = value;
    this.container?.style.setProperty('--progress-index', String(value));
  }

  /** Relee los links del nav y resuelve sus anchors en el documento. */
  reset() {
    if (!this.container) return;

    this._enabled = true;
    this.items = [];
    this.total = 0;
    this._activeIndex = -1;
    this.progress = 0;

    const links = [...GetBy.selector('a[href^="#"]', this.container)];

    links.forEach((link) => {
      const id = (link.getAttribute('href') || '').replace(/^#/, '').trim();
      if (!id) return;

      const dom = document.getElementById(id);
      if (!dom) {
        console.warn(`[IndexBar] anchor #${id} not found`);
        return;
      }

      const el = link.closest('.item') || link;

      this.total = this.items.push({
        id,
        link,
        el,
        dom,
        progressBar: 0,
        domSize: { pos0: 0, pos1: 0 },
      });
    });

    this.updateSizes();
    this._setActive(0);
  }

  updateSizes() {
    if (!this._enabled || !this.total) return;

    const scrollY = Scroll.y || 0;
    const offset = (Metrics.OFFSET || 0) * 1.2;
    const linkHeights = [];

    // Fin del contenido: bottom del padre en coords de documento → Scroll.y al llegar al final
    const contentBottom = this.content
      ? this.content.getBoundingClientRect().bottom + scrollY
      : document.documentElement.scrollHeight;

    const contentEndScroll = Math.max(0, contentBottom - Metrics.HEIGHT);

    for (let i = 0; i < this.total; i++) {
      const item = this.items[i];
      const next = this.items[i + 1];
      const isLast = !next;

      const domRect = item.dom.getBoundingClientRect();
      const linkRect = item.link.getBoundingClientRect();

      item.domSize.pos0 = i === 0 ? 0 : domRect.top + scrollY - offset;
      item.domSize.pos1 = isLast
        ? contentEndScroll
        : next.dom.getBoundingClientRect().top + scrollY - offset;

      // Evita rangos invertidos / cero
      if (item.domSize.pos1 <= item.domSize.pos0) {
        item.domSize.pos1 = item.domSize.pos0 + 1;
      }

      linkHeights.push(Math.max(1, linkRect.height));
    }

    const track = linkHeights.reduce((a, b) => a + b, 0) || 1;
    this.items.forEach((item, i) => {
      item.progressBar = linkHeights[i] / track;
    });
  }

  loop() {
    if (!this._enabled || !this.items[0]) return;

    let progress = 0;
    let activeIndex = 0;
    const y = Scroll.y || 0;
    const atEnd = Scroll.limit != null && y >= Scroll.limit - 1;

    for (let i = 0; i < this.total; i++) {
      const item = this.items[i];
      let p = Maths.normalize(item.domSize.pos1, item.domSize.pos0, y);
      p = Math.min(1, Math.max(0, p));

      // Último capítulo: si ya no hay más scroll, progreso completo
      if (i === this.total - 1 && atEnd) p = 1;

      progress += item.progressBar * p;

      // Activo = último ítem cuyo rango ha empezado
      if (y >= item.domSize.pos0) activeIndex = i;
    }

    if (atEnd) activeIndex = this.total - 1;

    this.progress = Math.min(1, progress);
    this._setActive(activeIndex);
  }

  _setActive(index) {
    if (index === this._activeIndex) return;
    this._activeIndex = index;

    this.items.forEach((item, i) => {
      item.el.classList.toggle('--active', i === index);
    });
  }

  resize() {
    if (!this._enabled) return;
    this.updateSizes();
  }

  dispose() {
    this.items.forEach((item) => item.el.classList.remove('--active'));
    this.items = [];
    this.total = 0;
    this._progress = 0;
    this._activeIndex = -1;
    this._enabled = false;
    this.container = null;
  }
}
